import React, { FC, useState, useCallback } from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { ErrorInfo } from 'react';
import { logErrorToDatadog, getDatadogSessionId } from './helpers/errorLogger';
import type { ErrorBoundaryProps } from './types';
import DefaultErrorFallback from './ErrorFallback';
import { navigateBack, Feedback, navigateTo } from '@helpers';

const ErrorBoundary: FC<ErrorBoundaryProps> = ({ children, screenName, FallbackComponent, onError, resetKey }) => {
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const [sessionId, setSessionId] = useState<string | undefined>();

  const FallbackToRender = FallbackComponent || DefaultErrorFallback;

  const handleError = useCallback((error: unknown, info: ErrorInfo) => {
    setErrorInfo(info);
    const typedError = error instanceof Error ? error : new Error(String(error));
    onError?.(typedError, info);

    getDatadogSessionId()
      .then(sid => {
        setSessionId(sid);
        logErrorToDatadog(typedError, info, screenName, sid);
      })
      .catch(() => {
        logErrorToDatadog(typedError, info, screenName);
      });
  }, [screenName, onError]);

  const handleReset = useCallback(() => {
    setErrorInfo(null);
    setSessionId(undefined);
  }, []);

  return (
    <ReactErrorBoundary
      resetKeys={resetKey !== undefined ? [resetKey] : undefined}
      onError={handleError}
      onReset={handleReset}
      fallbackRender={({ error, resetErrorBoundary }) => {
        const typedError = error instanceof Error ? error : new Error(String(error));
        return (
          <FallbackToRender
            error={typedError}
            errorInfo={errorInfo}
            screenName={screenName}
            sessionId={sessionId}
            onRetry={resetErrorBoundary}
            onGoBack={() => { resetErrorBoundary(); navigateBack(); }}
            onReportProblem={() => { Feedback({ type: 'error' }); }}
            onGoHome={() => { resetErrorBoundary(); navigateTo('Authorized'); }}
          />
        );
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
};

export default ErrorBoundary;
